// Guided breathing for minutes habits: a pattern is a loop of timed steps the
// pacer animates (in grows the circle, out shrinks it, hold keeps it).

export type BreathSide = 'left' | 'right'
export type BreathStep = { kind: 'in' | 'hold' | 'out'; seconds: number; side?: BreathSide }
export type BreathPatternId = 'box' | 'relax478' | 'nadi' | 'calm'

export const BREATH_PATTERNS: Record<BreathPatternId, readonly BreathStep[]> = {
  box: [
    { kind: 'in', seconds: 4 },
    { kind: 'hold', seconds: 4 },
    { kind: 'out', seconds: 4 },
    { kind: 'hold', seconds: 4 },
  ],
  relax478: [
    { kind: 'in', seconds: 4 },
    { kind: 'hold', seconds: 7 },
    { kind: 'out', seconds: 8 },
  ],
  nadi: [
    { kind: 'in', seconds: 4, side: 'left' },
    { kind: 'hold', seconds: 4 },
    { kind: 'out', seconds: 4, side: 'right' },
    { kind: 'in', seconds: 4, side: 'right' },
    { kind: 'hold', seconds: 4 },
    { kind: 'out', seconds: 4, side: 'left' },
  ],
  calm: [
    { kind: 'in', seconds: 4 },
    { kind: 'out', seconds: 6 },
  ],
}

export const BREATH_PATTERN_IDS = Object.keys(BREATH_PATTERNS) as BreathPatternId[]

export const isBreathPatternId = (s: unknown): s is BreathPatternId =>
  typeof s === 'string' && s in BREATH_PATTERNS

const BREATHY = /breath|pranayama|nadi|respir|呼吸/i
const RELAX_478 = /\b4\s*[-–‐·/:. ]\s*7\s*[-–‐·/:. ]\s*8\b/
const NADI = /nadi|nostril|narine|narina|fosas? nasal|片鼻|鼻孔/i
const BOX = /\bbox|square|cuadrad|carrée|quadrad|ボックス|箱式|方形/i

// The pattern a habit title suggests, or null when it isn't a breathing habit.
export function breathPatternFor(title: string): BreathPatternId | null {
  const s = title.split('///')[0] ?? ''
  if (!BREATHY.test(s)) return null
  if (RELAX_478.test(s)) return 'relax478'
  if (NADI.test(s)) return 'nadi'
  if (BOX.test(s)) return 'box'
  return 'calm'
}

export const cycleMs = (steps: readonly BreathStep[]) =>
  steps.reduce((n, s) => n + s.seconds * 1000, 0)

// Circle size 0 (empty lungs) .. 1 (full) at the start of each step.
function startLevels(steps: readonly BreathStep[]) {
  const levels: number[] = []
  let level = steps.some((s) => s.kind === 'out') ? 0 : 1
  for (const s of steps) {
    levels.push(level)
    if (s.kind === 'in') level = 1
    else if (s.kind === 'out') level = 0
  }
  return levels
}

const ease = (p: number) => (1 - Math.cos(Math.PI * p)) / 2

export type BreathPhase = {
  index: number
  step: BreathStep
  round: number
  // 0..1 through the step.
  progress: number
  remainingMs: number
  // 0..1 circle size.
  level: number
}

export function breathPhaseAt(steps: readonly BreathStep[], ms: number): BreathPhase {
  const total = cycleMs(steps)
  const t = Math.max(0, ms)
  const round = Math.floor(t / total) + 1
  let at = t % total
  let index = 0
  while (at >= steps[index].seconds * 1000) {
    at -= steps[index].seconds * 1000
    index++
  }
  const step = steps[index]
  const len = step.seconds * 1000
  const progress = at / len
  const from = startLevels(steps)[index]
  const to = step.kind === 'in' ? 1 : step.kind === 'out' ? 0 : from
  return {
    index,
    step,
    round,
    progress,
    remainingMs: len - at,
    level: from + (to - from) * ease(progress),
  }
}
